import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { 
  ChevronLeft, Clock, MapPin, Phone, FileText, 
  CheckCircle, AlertCircle, Navigation, User,
  Calendar, Play, Square, Loader2, MessageSquare
} from "lucide-react";

export default function VisitDetail() {
  const { visitId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clockingOut, setClockingOut] = useState(false);

  useEffect(() => {
    loadVisit();
  }, [visitId]);

  const loadVisit = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employee/visits/${visitId}`);
      setVisit(data);
    } catch (err) {
      console.error("Failed to load visit:", err);
      setError(err.response?.data?.detail || "Failed to load visit");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy_meters: position.coords.accuracy,
          });
        },
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const handleClockOut = async () => {
    setClockingOut(true);
    setError(null);
    
    try {
      const gps = await getCurrentPosition();
      await api.post("/employee/clock-out", {
        visit_id: visitId,
        gps,
        device_info: navigator.userAgent,
      });
      await loadVisit();
    } catch (err) {
      setError(err.response?.data?.detail || "Clock out failed");
    } finally {
      setClockingOut(false);
    }
  };

  const openMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, "_blank");
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: "bg-gray-100 text-gray-700",
      clocked_in: "bg-emerald-100 text-emerald-700",
      in_progress: "bg-emerald-100 text-emerald-700",
      clocked_out: "bg-blue-100 text-blue-700",
      completed: "bg-blue-100 text-blue-700",
      care_note_pending: "bg-amber-100 text-amber-700",
      supervisor_review: "bg-purple-100 text-purple-700",
      approved: "bg-emerald-100 text-emerald-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#06372B]" />
      </div>
    );
  }

  if (error || !visit) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-3" />
          <div className="text-gray-900 font-medium">{error || "Visit not found"}</div>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-[#06372B] font-medium"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  const client = visit.client;
  const clientName = client ? `${client.first_name} ${client.last_name}` : "Client";
  const isClockedIn = visit.status === "clocked_in" || visit.status === "in_progress";
  const isCompleted = ["clocked_out", "completed", "care_note_pending", "supervisor_review", "approved"].includes(visit.status);
  const needsCareNotes = ["clocked_out", "care_note_pending"].includes(visit.status) && !visit.care_note;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-[#06372B] text-white px-4 py-5 sticky top-0 z-40">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-white/10">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="font-semibold text-lg">Visit Details</div>
            <div className="text-sm text-white/70">{visit.visit_date || "Today"}</div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(visit.status)}`}>
            {visit.status.replace(/_/g, " ")}
          </span>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        {/* Client Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <User className="w-4 h-4 text-gray-400" />
            <h2 className="font-semibold">Client Information</h2>
          </div>
          <div className="p-4 space-y-3">
            <div className="font-semibold text-lg text-gray-900">{clientName}</div>
            
            {client?.address && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-400 mt-1 shrink-0" />
                <div>
                  <div className="text-gray-700">{client.address}</div>
                  <button
                    onClick={() => openMaps(client.address)}
                    className="text-sm text-[#06372B] font-medium mt-1 flex items-center gap-1"
                  >
                    <Navigation className="w-3 h-3" />
                    Open in Maps
                  </button>
                </div>
              </div>
            )}
            
            {client?.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <a href={`tel:${client.phone}`} className="text-[#06372B] font-medium">
                  {client.phone}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Visit Times */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <h2 className="font-semibold">Visit Times</h2>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Scheduled</span>
              <span className="font-medium">
                {visit.scheduled_start_time || "N/A"} - {visit.scheduled_end_time || "N/A"}
              </span>
            </div>
            
            {visit.clock_in && (
              <div className="flex justify-between">
                <span className="text-gray-500">Clocked In</span>
                <span className="font-medium text-emerald-600">
                  {new Date(visit.clock_in.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            )}
            
            {visit.clock_out && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-500">Clocked Out</span>
                  <span className="font-medium text-blue-600">
                    {new Date(visit.clock_out.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-100">
                  <span className="text-gray-900 font-medium">Total Hours</span>
                  <span className="font-semibold text-[#06372B]">
                    {visit.total_hours?.toFixed(2) || "0.00"} hrs
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Clock Out Button (if clocked in) */}
        {isClockedIn && (
          <button
            onClick={handleClockOut}
            disabled={clockingOut}
            className="w-full py-4 px-6 rounded-xl bg-red-600 text-white font-semibold flex items-center justify-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-60"
            data-testid="clock-out-btn"
          >
            {clockingOut ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Square className="w-5 h-5" />
            )}
            Clock Out
          </button>
        )}

        {/* Care Notes Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              <h2 className="font-semibold">Care Notes</h2>
            </div>
            {visit.care_note && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                visit.care_note.status === "approved" 
                  ? "bg-emerald-100 text-emerald-700"
                  : visit.care_note.status === "submitted"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-gray-100 text-gray-600"
              }`}>
                {visit.care_note.status}
              </span>
            )}
          </div>
          <div className="p-4">
            {visit.care_note ? (
              <div className="space-y-3">
                <div className="text-sm text-gray-500">
                  {visit.care_note.guided_entries?.length || 0} entries documented
                </div>
                <Link
                  to={`/employee/visit/${visitId}/care-notes`}
                  className="w-full py-3 px-4 rounded-xl bg-gray-100 text-gray-700 font-medium flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors"
                  data-testid="view-care-notes"
                >
                  <FileText className="w-4 h-4" />
                  View Care Notes
                </Link>
              </div>
            ) : isCompleted ? (
              <div className="space-y-3">
                {needsCareNotes && (
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 text-amber-800">
                    <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      Please complete your care notes for this visit.
                    </div>
                  </div>
                )}
                <Link
                  to={`/employee/visit/${visitId}/care-notes`}
                  className="w-full py-3 px-4 rounded-xl bg-[#06372B] text-white font-medium flex items-center justify-center gap-2 hover:bg-[#0a4a3a] transition-colors"
                  data-testid="add-care-notes"
                >
                  <FileText className="w-4 h-4" />
                  Add Care Notes
                </Link>
              </div>
            ) : (
              <div className="text-sm text-gray-500 text-center py-4">
                Care notes can be added after clocking out.
              </div>
            )}
          </div>
        </div>

        {/* Services Provided (placeholder for future) */}
        {visit.services_provided?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-gray-400" />
              <h2 className="font-semibold">Services Provided</h2>
            </div>
            <div className="p-4">
              <div className="flex flex-wrap gap-2">
                {visit.services_provided.map((service, idx) => (
                  <span key={idx} className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm">
                    {service}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
